import React, { useState, useEffect } from "react";
import { useCountryStore } from "../../../store/countryStore.js";
import { useUserStore } from "../../../store/userStore.js";
import dayjs from "dayjs";


import EditProfileBoxLabel from "../../../components/label/editprofileboxlabel";
import EditProfileInput from "../../../components/inputbox/editprofileinput";
import EditProfileDropdown from "../../../components/dropdown/editprofiledropdown";
import EditProfileDatepicker from "../../../components/datepicker/editprofiledatepicker";
import EditProfilePhoneInput from "../../../components/phoneinput/editprofilephoneinput";
import EditProfileMultipleSelect from "../../../components/dropdown/editprofilemultipleselection";
import PrimaryButton from "../../../components/button/primarybutton";
import Loader from "../../../components/loader/loader";
import Spacer from "../../../components/spacer/spacer";

import ErrorText from "../../../components/errortext/errortext";

import images from "../../../assets";

import {
  ethnicOrigin,
  genderItems,
  homeType,
  maritalStatus,
  profileCreatedBy,
  relocationPlan,
  residencyStatus,
  spokenLanguages,
} from "../../../utils/arrayitems";

import TabHeading from "./tabheading";

import "./styles.css";

const BasicInfo = () => {
  const { countries, states, getStates } = useCountryStore();
  const { user, isUserLoading, getUser, updateUser } = useUserStore();

  const [state, setState] = useState({
    email: "",
    firstName: "",
    lastName: "",
    gender: "",
    dateOfBirth: "",
    phone: "",
    country: "",
    stateProvince: "",
    homeType: "",
    residencyStatus: "",
    relocationPlan: "",
    ethnicOrigin: "",
    maritalStatus: "",
    profileCreatedBy: "",
    spokenLanguage: [],
  });
  const [error, setError] = useState({
    name: "",
    email: "",
    gender: "",
    dateOfBirth: "",
    phone: "",
    country: "",
    stateProvince: "",
    homeType: "",
    residencyStatus: "",
    ethnicOrigin: "",
    maritalStatus: "",
    profileCreatedBy: "",
    spokenLanguage: [],
  });

  useEffect(() => {
    const payload = { country: [state?.country] };
    getStates(payload);
  }, [getStates, state?.country]);

  useEffect(() => {
    setState({
      ...state,
      email: user?.email ?? "",
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      gender: user?.gender ?? "",
      dateOfBirth: user?.dateOfBirth ?? "",
      phone: user?.phone ?? "",
      country: user?.country ?? "",
      stateProvince: user?.stateProvince ?? "",
      homeType: user?.homeType ?? "",
      residencyStatus: user?.residencyStatus ?? "",
      relocationPlan: user?.relocationPlan ?? "",
      ethnicOrigin: user?.ethnicOrigin ?? "",
      maritalStatus: user?.maritalStatus ?? "",
      profileCreatedBy: user?.profileCreatedBy ?? "",
      spokenLanguage: user?.spokenLanguage ?? [],
    });
  }, [isUserLoading]);

  const getprofileData = () => {
    const payload = { username: user?.username };
    getUser(payload);
  };

  const handleSubmit = () => {
    if (!state?.firstName || !state?.lastName) {
      setError({
        ...error,
        name: "Name cannot be empty",
      });
    } else if (!state?.stateProvince) {
      setError({
        ...error,
        stateProvince: "State cannot be empty",
      });
    } else if (!state?.homeType) {
      setError({
        ...error,
        homeType: "Home Type cannot be empty",
      });
    } else if (!state?.residencyStatus) {
      setError({
        ...error,
        residencyStatus: "Residency status cannot be empty",
      });
    } else if (!state?.ethnicOrigin) {
      setError({
        ...error,
        ethnicOrigin: "Ethnic origin cannot be empty",
      });
    } else if (!state?.maritalStatus) {
      setError({
        ...error,
        maritalStatus: "Marital status cannot be empty",
      });
    } else if (!state?.profileCreatedBy) {
      setError({
        ...error,
        profileCreatedBy: "Profile created by cannot be empty",
      });
    } else if (state?.spokenLanguage?.length === 0) {
      setError({
        ...error,
        spokenLanguage: "Spoken language cannot be empty",
      });
    } else {
      const payload = { username: user?.username, data: state };
      updateUser(payload, () => {
        getprofileData();
      });
    }
  };

  return (
    <>
      <TabHeading
        image={images.editProfileBasicInfo}
        title="Basic Information"
        subtitle="Complete to easily find your ideal partner!"
      />
      <div className="edit-profile-content">
        {isUserLoading ? (
          <Loader />
        ) : (
          <>
            {/* email */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Email" required={true} />
            </div>
            <Spacer height="8px" />
            <EditProfileInput
              placeholder="Email"
              onChange={(e) => {
                setState({ ...state, email: e.target.value });
                setError({ ...error, email: "" });
              }}
              value={state.email || undefined}
            />
            {error?.email && <ErrorText text={error?.email} />}

            <Spacer height="12px" />

            {/* name */}
            <div className="edit-profile-name-container">
              <div className="edit-profile-name">
                <EditProfileBoxLabel label="First Name" />
                <Spacer height="8px" />
                <EditProfileInput
                  placeholder="First name"
                  onChange={(e) =>
                    setState({ ...state, firstName: e.target.value })
                  }
                  value={state.firstName || undefined}
                />
              </div>
              <div className="edit-profile-name">
                <EditProfileBoxLabel label="Last Name" />
                <Spacer height="8px" />
                <EditProfileInput
                  placeholder="Last name"
                  onChange={(e) =>
                    setState({ ...state, lastName: e.target.value })
                  }
                  value={state.lastName || undefined}
                />
              </div>
            </div>

            {error?.name && <ErrorText text={error?.name} />}
            <Spacer height="12px" />

            {/* gender */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="I am a" required={true} />
            </div>
            <Spacer height="8px" />
            <EditProfileDropdown
              placeholder="Gender"
              data={genderItems}
              onChange={(e) => {
                setState({ ...state, gender: e });
                setError({ ...error, gender: "" });
              }}
              value={state.gender || undefined}
            />
            {error?.gender && <ErrorText text={error?.gender} />}
            <Spacer height="12px" />

            {/* date of birth */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Date of Birth" required={true} />
            </div>
            <Spacer height="8px" />
            <EditProfileDatepicker
              placeholder="Select Date"
              onChange={(date, dateString) => {
                setState({ ...state, dateOfBirth: dateString });
                setError({ ...error, dateOfBirth: "" });
              }}
              value={
                state?.dateOfBirth
                  ? dayjs(state?.dateOfBirth, "YYYY-MM-DD")
                  : null
              }
            />
            {error?.dateOfBirth && <ErrorText text={error?.dateOfBirth} />}
            <Spacer height="12px" />

            {/* phone input */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Phone" required={true} />
            </div>
            <Spacer height="8px" />
            <EditProfilePhoneInput
              placeholder="Please enter your phone number"
              onChange={(e) => {
                setState({ ...state, phone: e });
                setError({ ...error, phone: "" });
              }}
              value={state.phone || undefined}
            />
            {error?.phone && <ErrorText text={error?.phone} />}
            <Spacer height="12px" />

            {/* country */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Country" required={true} />
            </div>
            <Spacer height="8px" />
            <EditProfileDropdown
              placeholder="Please select"
              data={countries}
              list="country"
              onChange={(e) => {
                const country = JSON.parse(e);
                setState({ ...state, country: country.name });
                setError({ ...error, country: "" });
              }}
              value={state.country || undefined}
            />
            {error?.country && <ErrorText text={error?.country} />}

            <Spacer height="12px" />

            {/* state */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="State/Province" required={true} />
            </div>
            <Spacer height="8px" />
            <EditProfileDropdown
              placeholder="Please select"
              data={states}
              list="stateProvince"
              onChange={(e) => {
                setState({ ...state, stateProvince: e });
                setError({ ...error, stateProvince: "" });
              }}
              value={state.stateProvince || undefined}
            />
            {error?.stateProvince && <ErrorText text={error?.stateProvince} />}
            <Spacer height="12px" />

            {/* home type */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Home Type" required={true} />
            </div>
            <Spacer height="8px" />
            <EditProfileDropdown
              placeholder="Please select"
              data={homeType}
              onChange={(e) => {
                setState({ ...state, homeType: e });
                setError({ ...error, homeType: "" });
              }}
              value={state.homeType || undefined}
            />
            {error?.homeType && <ErrorText text={error?.homeType} />}
            <Spacer height="12px" />

            {/* residency status */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Residency Status" required={true} />
            </div>
            <Spacer height="8px" />
            <EditProfileDropdown
              placeholder="Please select"
              data={residencyStatus}
              onChange={(e) => {
                setState({ ...state, residencyStatus: e });
                setError({ ...error, residencyStatus: "" });
              }}
              value={state.residencyStatus || undefined}
            />
            {error?.residencyStatus && (
              <ErrorText text={error?.residencyStatus} />
            )}
            <Spacer height="12px" />

            {/* Relocation Plan */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Relocation Plan" />
            </div>
            <Spacer height="8px" />
            <EditProfileDropdown
              placeholder="Please select"
              data={relocationPlan}
              onChange={(e) => setState({ ...state, relocationPlan: e })}
              value={state.relocationPlan || undefined}
            />

            <Spacer height="12px" />

            {/* Ethnic Origin */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Ethnic Origin" required={true} />
            </div>
            <Spacer height="8px" />
            <EditProfileDropdown
              placeholder="Please select"
              data={ethnicOrigin}
              onChange={(e) => {
                setState({ ...state, ethnicOrigin: e });
                setError({ ...error, ethnicOrigin: "" });
              }}
              value={state.ethnicOrigin || undefined}
            />
            {error?.ethnicOrigin && <ErrorText text={error?.ethnicOrigin} />}
            <Spacer height="12px" />

            {/* Marital Status */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Marital Status" required={true} />
            </div>
            <Spacer height="8px" />
            <EditProfileDropdown
              placeholder="Please select"
              data={maritalStatus}
              onChange={(e) => {
                setState({ ...state, maritalStatus: e });
                setError({ ...error, maritalStatus: "" });
              }}
              value={state.maritalStatus || undefined}
            />
            {error?.maritalStatus && <ErrorText text={error?.maritalStatus} />}
            <Spacer height="12px" />

            {/* Profile Created by */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Profile Created by" required={true} />
            </div>
            <Spacer height="8px" />
            <EditProfileDropdown
              placeholder="Please select"
              data={profileCreatedBy}
              onChange={(e) => {
                setState({ ...state, profileCreatedBy: e });
                setError({ ...error, profileCreatedBy: "" });
              }}
              value={state.profileCreatedBy || undefined}
            />
            {error?.profileCreatedBy && (
              <ErrorText text={error?.profileCreatedBy} />
            )}
            <Spacer height="12px" />

            {/* Spoken Language */}
            <div className="edit-profile-label-container">
              <EditProfileBoxLabel label="Spoken Language" required={true} />
            </div>
            <Spacer height="8px" />
            <EditProfileMultipleSelect
              placeholder="Please select"
              data={spokenLanguages}
              onChange={(e) => {
                setState({ ...state, spokenLanguage: e });
                setError({ ...error, spokenLanguage: "" });
              }}
              value={state.spokenLanguage || undefined}
            />
            {error?.spokenLanguage && (
              <ErrorText text={error?.spokenLanguage} />
            )}
          </>
        )}
        <Spacer height="32px" />
        <PrimaryButton
          text="Save & Continue"
          onClick={handleSubmit}
          disabled={isUserLoading}
        />
      </div>
    </>
  );
};

export default BasicInfo;








